import { zodResolver } from '@hookform/resolvers/zod';
import { ContentSwitch } from '@ironhack/design-system2/components/content-switch';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { FormField } from '@ironhack/design-system2/components/form-field';
import { filter, map, omit, pick, pipe, prop } from 'ramda';
import { type ReactElement, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';

import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { PhoneField } from '@/app/components/client/phone-field';
import { SelectField } from '@/app/components/client/select-field';
import { TextareaField } from '@/app/components/client/textarea-field';
import { FormSubmit } from '@/app/components/server/form-submit';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import {
  type ThankYouSession,
  useThankYouSession,
} from '@/hooks/use-thank-you-session';
import { type DatoCampus } from '@/lib/datocms/types';
import { contactFormTags, sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { regionToCampusMap } from '@/lib/utils';
import { getContactSchema } from '@/lib/utils/form-validations/forms/contact-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import { type NameInputs } from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { type Form as FormType } from '../../modules/1-form';

type FormData = {
  cToken: string;
  message: string;
  newsletter: boolean;
  phone: PhoneInputs;
  selection: {
    campus: string;
    courseType: 'in_person' | 'online';
    track: string;
  };
} & EmailInput &
  NameInputs;

type Props = {
  contactForm: FormType;
  onSuccess: (s: ThankYouSession) => void;
  onStep2: () => void;
} & FlexProps;

export const Form = (props: Props): ReactElement => {
  const { contactForm, onSuccess, onStep2, ...flexProps } = props;
  const {
    buttonText,
    campuses,
    campusLabel,
    campusPlaceholder,
    courseLabel,
    coursePlaceholder,
    courses,
    courseTypeLabel,
    errorMessage,
    inPersonOption,
    inquiryLabel,
    inquiryPlaceholder,
    legalText,
    newsletterLabel,
    onlineOption,
    phoneLabel,
    phonePlaceholder,
  } = contactForm;
  const {
    params: { language, region },
    pageData: { gaCategory },
  } = usePageContext();
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [formEmail] = useSessionStorage<string>('formEmail');
  const [websiteCampusPage] = useSessionStorage(
    'campus',
    regionToCampusMap[region][0]
  );
  const [showInquiry, setshowInquiry] = useState(true);

  const formSchema = getContactSchema(contactForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      email: formEmail,
      message: '',
      newsletter: false,
      phone: getPhoneDefaults(region),
      selection: {
        campus: websiteCampusPage,
        courseType: websiteCampusPage === 'rmt' ? 'online' : 'in_person',
        track: '',
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    clearErrors,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    trigger,
    watch,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const courseType = watch('selection.courseType');
  const formRequest = useFormRequest();
  const [previousPageType] = useSessionStorage<string>('pageTypePrevious');
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const [, setThankYouSession] = useThankYouSession();
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    const {
      email,
      firstName,
      newsletter,
      phone: { phoneNumber, country },
      selection: { campus: selectedCampus, courseType: selectedType, track },
    } = formData;

    try {
      const response = await formRequest({
        data: {
          ...omit(['newsletter', 'selection'], formData),
          bootcamp: `${track.toUpperCase()}FT`,
          campus: selectedType === 'in_person' ? selectedCampus : 'rmt',
          emailOptIn: newsletter,
          language,
          region,
          websiteCampusPage,
        },
        formType: 'contact',
      });

      const formReferrer = sessionStorage.getItem('formReferrer') || 'default';
      const defaultEventCategory =
        previousPageType && previousPageType !== 'undefined'
          ? previousPageType
          : gaCategory;
      const { eventAction, eventCategory = defaultEventCategory } =
        contactFormTags[formReferrer];

      sendEvent({
        campus: selectedCampus,
        course: track,
        email,
        eventAction,
        eventCategory,
        eventLabel: `${selectedCampus}::${track}`,
        pageType: eventCategory,
      });
      setGtmLocalStorage({
        applicationData: {
          campus: selectedCampus,
          course: track,
          phoneNumber,
          ...pick(['email', 'firstName', 'lastName'], formData),
        },
        countryName: country,
      });

      const thankYouSession = await setThankYouSession({ firstName, response });
      onSuccess(thankYouSession);
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Flex direction="column" {...flexProps}>
      <FormProvider {...formMethods}>
        <form id="contactForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" direction="column">
            {step === 1 && (
              <>
                <SelectField
                  error={errors.selection?.track?.message}
                  isRequired
                  label={courseLabel}
                  name="selection.track"
                  options={courses.map((course) => ({
                    key: course.code,
                    text: course.name,
                  }))}
                  placeholder={coursePlaceholder}
                />
                <FormField
                  error=""
                  label={courseTypeLabel}
                  mt={3}
                  name="courseType"
                >
                  <Controller
                    control={control}
                    defaultValue="online"
                    name="selection.courseType"
                    render={({ field: { onChange, value, ref } }) => (
                      <ContentSwitch
                        onChange={onChange}
                        options={[
                          { key: 'online', text: onlineOption },
                          { key: 'in_person', text: inPersonOption },
                        ]}
                        ref={ref}
                        value={value}
                      />
                    )}
                  />
                </FormField>
                {courseType === 'in_person' && (
                  <SelectField
                    error={errors.selection?.campus?.message}
                    isRequired
                    label={campusLabel}
                    mt={4}
                    name="selection.campus"
                    options={pipe(
                      filter(
                        prop('isShownOnWebsite') as (x: DatoCampus) => boolean
                      ),
                      map(({ code, name }) => ({
                        key: code,
                        text: name,
                      }))
                    )(campuses)}
                    placeholder={campusPlaceholder}
                  />
                )}
                <ContactDetailsFields
                  fields={pick(
                    [
                      'chooseBetween',
                      'completeFields',
                      'emailLabel',
                      'emailPlaceholder',
                      'firstNameLabel',
                      'firstNamePlaceholder',
                      'lastNameLabel',
                      'lastNamePlaceholder',
                      'phoneLabel',
                      'phonePlaceholder',
                    ],
                    contactForm
                  )}
                  mt={4}
                  onGoogleClick={() => {
                    setFormFeedback(null);
                    setIsGoogleSubmitting(true);
                  }}
                  onGoogleError={() => {
                    setIsGoogleSubmitting(false);
                    setFormFeedback({
                      status: 'error',
                      message: errorMessage,
                    });
                  }}
                  onGoogleSuccess={async (googleData) => {
                    setIsGoogleSubmitting(false);
                    const isValid = await trigger('selection');
                    if (isValid) {
                      setValue('email', googleData.email);
                      setValue('firstName', googleData.firstName);
                      setValue('lastName', googleData.lastName);
                      clearErrors();
                      if (getValues('message')) {
                        setshowInquiry(false);
                      }
                      setStep(2);
                      onStep2();
                    }
                  }}
                  setFormValues={false}
                  withPhone
                />
              </>
            )}
            {step === 2 && (
              <PhoneField
                error={
                  errors.phone?.message || errors.phone?.phoneNumber?.message
                }
                isRequired
                label={phoneLabel}
                name="phone"
                placeholder={phonePlaceholder}
              />
            )}
            {showInquiry && (
              <TextareaField
                error={errors.message?.message}
                isRequired
                key="message"
                label={inquiryLabel}
                mt={2}
                name="message"
                placeholder={inquiryPlaceholder}
              />
            )}

            <TurnstileFormToken
              language={language}
              onVerify={setCaptchaToken}
            />
            <FormSubmit
              buttonText={buttonText}
              isLoading={isSubmitting || isGoogleSubmitting}
              legalText={legalText}
              newsletterLabel={newsletterLabel}
              withNewsletter
            />
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
